import { useState } from "react";
import type { ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCatalogFacets } from "../../../../products/hooks/useCatalogFacets";
import { IconArrowRight, IconBolt, IconCheck, IconChevronDown, IconChevronRight, IconChevronUp, IconCircleCheck, IconDeviceLaptop, IconShoppingCart } from "@tabler/icons-react";
import { Button } from "../../../../../components/common/button";
import { ClampText, Heading, Text } from "../../../../../components/common/typography";
import { getProductDetailsUrl } from "../../../../products/utils/get-product-details-url";
import { GatedPrice } from "../../../../../components/common/gated-price";
import { Chip } from "../../../../../components/common/chip/chip";
import { AppRoutes } from "../../../../../common/routes/app_routes";
import { useCartStore } from "../../../../../hooks/stores/useCartStore";
import { useCartUiStore } from "../../../../../hooks/stores/useCartUiStore";
import { productVariantsService } from "../../../../api_client";
import { useWidgetProductIds } from "../../../hooks/useWidgetProductIds";
import { showSuccessToast, showErrorToast } from "../../../../../utils/toasts";
import "../../../../landing/presentation/pages/landing-page.css";

// Widget dispatcher. The parser recognises {{type:{json}}} tokens and hands
// each one here. New widgets: add a case + component. The default `null`
// keeps unknown widgets invisible instead of crashing.

export function WidgetRenderer({
  widgetType,
  data,
  onSendMessage,
}: {
  widgetType: string;
  data: unknown;
  onSendMessage?: (text: string, productId?: number) => void;
}) {
  switch (widgetType) {
    case "recommendations":
      return <RecommendationsWidget data={data as RecommendationsData} onSendMessage={onSendMessage} />;
    case "results":
      return <ResultsWidget data={data as ResultsData} />;
    case "barChart":
      return <BarChartWidget data={data as ChartData} />;
    case "lineChart":
      return <LineChartWidget data={data as ChartData} />;
    case "reportCard":
      return <ReportCardWidget data={data as ReportCardData} />;
    case "textOptions":
      return <TextOptionsWidget data={data as TextOptionsData} />;
    case "action":
      return <ActionWidget data={data as ActionData} />;
    case "comparisonTable":
      return <ComparisonTableWidget data={data as ComparisonTableData} />;
    case "refineSearch":
      return <RefineSearchWidget data={data as RefineSearchData} onSendMessage={onSendMessage} />;
    case "productDetail":
      return <ProductDetailWidget data={data as ProductDetailData} onSendMessage={onSendMessage} />;
    case "productCompare":
      return <ProductCompareWidget data={data as ProductCompareData} onSendMessage={onSendMessage} />;
    default:
      return null;
  }
}

// ── Shared types ─────────────────────────────────────────────────────

interface RecommendationsData {
  title?: string;
  products?: Array<{ name?: string; specs?: string; price?: string; badge?: string; image?: string; variantId?: number; productId?: number }>;
  alternatives?: Array<{ name?: string; subLabel?: string; price?: string; image?: string; variantId?: number; productId?: number }>;
  notes?: string[];
}

interface RefineSearchData {
  title?: string;
  useCases?: string[];
  mustHaves?: string[];
  leadTimeOptions?: string[];
  budgetRange?: { min?: number; max?: number; currency?: string };
  moqRange?: { min?: number; max?: number; unit?: string };
}

interface ProductDetailData {
  badge?: string;
  name?: string;
  availability?: string;
  specLine?: string;
  specs?: Record<string, string>;
  price?: string;
  originalPrice?: string;
  savings?: string;
  image?: string;
  followUpQuestions?: string[];
}

interface ProductCompareData {
  badge?: string;
  availability?: string;
  products?: Array<{
    name?: string;
    specLine?: string;
    specs?: Record<string, string>;
    price?: string;
    originalPrice?: string;
    savings?: string;
    image?: string;
    productId?: number;
    variantId?: number;
  }>;
  followUpQuestions?: string[];
  note?: string;
}

interface ComparisonTableData {
  title?: string;
  products?: Array<{ name?: string; specs?: Record<string, string> }>;
  specOrder?: string[];
}

interface ResultsData {
  title?: string;
  rows?: Array<{ label?: string; value?: string; status?: "normal" | "high" | "low" }>;
}

interface ChartPoint {
  label?: string;
  value?: number;
}
interface ChartData {
  title?: string;
  unit?: string;
  points?: ChartPoint[];
  maxValue?: number;
}

interface ReportCardData {
  title?: string;
  points?: Array<{ x?: number; y?: number; latest?: boolean }>;
  metrics?: Array<{ label?: string; value?: string }>;
}

interface TextOptionsData {
  title?: string;
  subtitle?: string;
  paragraphs?: string[];
  bullets?: string[];
}

interface ActionData {
  label?: string;
  action?: "navigate_products" | "navigate_compare" | string;
}

// ── Product thumbnail with error fallback ────────────────────────────

function resolveImageUrl(image: string): string {
  if (image.startsWith("http://") || image.startsWith("https://")) return image;
  const base = (import.meta.env.VITE_BACKEND_HOST ?? "").replace(/\/+$/, "");
  return `${base}${image}`;
}

function ProductThumbnail({
  image,
  name,
  size,
  fallback,
}: {
  image?: string;
  name?: string;
  size: "sm" | "lg";
  fallback: ReactNode;
}) {
  const [error, setError] = useState(false);
  const cls = size === "sm"
    ? "flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-bgLight"
    : "flex h-[88px] w-[88px] shrink-0 items-center justify-center overflow-hidden rounded-xl bg-bgLight";

  return (
    <div className={cls}>
      {image && !error ? (
        <img
          src={resolveImageUrl(image)}
          alt={name ?? ""}
          className="h-full w-full object-contain"
          onError={() => setError(true)}
        />
      ) : fallback}
    </div>
  );
}

// ── Widget container ─────────────────────────────────────────────────

function WidgetCard({ title, badge, children }: { title?: string; badge?: string; children: React.ReactNode }) {
  return (
    <div className="flex w-full flex-col gap-3 rounded-2xl border border-borderDefault bg-bgWhite p-4">
      {badge ? (
        <div className="self-start">
          <Chip variant="aiSection">{badge}</Chip>
        </div>
      ) : null}
      {title ? (
        <Heading variant="h3" tone="dark">
          {title}
        </Heading>
      ) : null}
      {children}
    </div>
  );
}

// ── Recommendations (data-driven) ─────────────────────────────────────

function RecommendationsWidget({
  data,
  onSendMessage,
}: {
  data: RecommendationsData;
  onSendMessage?: (text: string, productId?: number) => void;
}) {
  const navigate = useNavigate();
  const addToCart = useCartStore((s) => s.addToCart);
  const playFlyToCart = useCartUiStore((s) => s.playFlyToCart);
  const [addingId, setAddingId] = useState<number | null>(null);
  const products = data.products ?? [];
  const notes = data.notes ?? [];
  const alternatives = data.alternatives ?? [];
  const resolveProductId = useWidgetProductIds([...products, ...alternatives]);

  // Compare page expects product template IDs in `?ids=` (it calls
  // useProductVariants(templateId)).
  const compareIds = products
    .map(resolveProductId)
    .filter((id): id is number => id != null);

  const handleAddToCart = async (p: typeof products[number], e: React.MouseEvent<HTMLButtonElement>) => {
    if (!p.variantId) return;
    setAddingId(p.variantId);
    try {
      const res = await productVariantsService.getProductVariantById(p.variantId);
      addToCart(res.data, 1);
      playFlyToCart({ source: e.currentTarget, image: p.image });
      showSuccessToast(`${p.name || "Product"} added to cart`);
    } catch {
      showErrorToast("Could not add to cart. Please try again.");
    } finally {
      setAddingId(null);
    }
  };

  const handleCompare = () => {
    const url = compareIds.length > 0
      ? `${AppRoutes.PRODUCTS_COMPARE}?ids=${compareIds.join(",")}&from=ai`
      : `${AppRoutes.PRODUCTS_COMPARE}?from=ai`;
    navigate(url);
  };

  if (products.length === 0 && alternatives.length === 0 && notes.length === 0) {
    return null;
  }

  // Responsive: 1 column on narrow chat / mobile, one column per populated
  // section (max 3) on wide screens — mirrors the screenshot layout.
  const sectionCount =
    (products.length > 0 ? 1 : 0) +
    (alternatives.length > 0 ? 1 : 0) +
    (notes.length > 0 ? 1 : 0);
  const gridCols =
    sectionCount >= 3 ? "lg:grid-cols-3" : sectionCount === 2 ? "lg:grid-cols-2" : "";

  const column = "flex flex-col gap-3 rounded-2xl border border-borderDefault bg-bgWhite p-4";

  return (
    <div className={`grid w-full grid-cols-1 gap-3 ${gridCols}`}>
      {products.length > 0 ? (
        <div className={column}>
          <Chip variant="aiSection">✦ Procurtal Recommendations</Chip>

          <div className="flex flex-col gap-3">
            {products.map((p, i) => {
              const detailsId = resolveProductId(p);
              const detailsUrl = detailsId
                ? getProductDetailsUrl(detailsId, p.variantId ?? null, AppRoutes.PRODUCT_DETAILS)
                : null;
              return (
              <div
                key={i}
                className="flex flex-col gap-3 rounded-xl border border-borderLight bg-bgLight/40 p-3"
              >
                <div className="flex gap-3">
                  {detailsUrl ? (
                    <Link to={detailsUrl} aria-label={`View ${p.name ?? "product"}`} className="hover:opacity-90 transition-opacity">
                      <ProductThumbnail
                        image={p.image}
                        name={p.name}
                        size="sm"
                        fallback={<IconDeviceLaptop size={28} className="text-textMuted" />}
                      />
                    </Link>
                  ) : (
                    <ProductThumbnail
                      image={p.image}
                      name={p.name}
                      size="sm"
                      fallback={<IconDeviceLaptop size={28} className="text-textMuted" />}
                    />
                  )}

                  <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                    <div className="flex items-start justify-between gap-2">
                      {detailsUrl ? (
                        <Link to={detailsUrl} className="min-w-0 flex-1 hover:text-brandPrimary transition-colors">
                          <ClampText variant="bodySmBold" tone="dark" lines={2}>
                            {p.name ?? "—"}
                          </ClampText>
                        </Link>
                      ) : (
                        <ClampText
                          variant="bodySmBold"
                          tone="dark"
                          lines={2}
                          className="min-w-0 flex-1"
                        >
                          {p.name ?? "—"}
                        </ClampText>
                      )}
                      {p.badge ? (
                        <span className="shrink-0 rounded-full bg-accentCyanTint px-2 py-0.5">
                          <Text variant="bodyXsBold" tone="inherit" inline>
                            {p.badge}
                          </Text>
                        </span>
                      ) : null}
                    </div>
                    {p.specs ? (
                      <ClampText variant="bodyXs" tone="muted" lines={2}>
                        {p.specs}
                      </ClampText>
                    ) : null}
                    {p.price ? (
                      <Text variant="bodySmBold" tone="dark">
                        <GatedPrice>{p.price}</GatedPrice>
                      </Text>
                    ) : null}
                  </div>
                </div>

                {(p.variantId || onSendMessage) ? (
                  <div className="flex flex-nowrap items-center gap-2">
                    {p.variantId ? (
                      <Button
                        variant="primary"
                        size="sm"
                        leftIcon={<IconShoppingCart size={14} />}
                        disabled={addingId === p.variantId}
                        onClick={(e) => handleAddToCart(p, e)}
                      >
                        {addingId === p.variantId ? "Adding…" : "Add to cart"}
                      </Button>
                    ) : null}
                    {onSendMessage ? (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => onSendMessage(`Tell me more about ${p.name}`, detailsId ?? undefined)}
                      >
                        Chat about
                      </Button>
                    ) : null}
                  </div>
                ) : null}
              </div>
              );
            })}
          </div>

          <div className="mt-auto pt-1">
            <Button variant="primary" fullWidth onClick={handleCompare}>
              Compare products
            </Button>
          </div>
        </div>
      ) : null}

      {alternatives.length > 0 ? (
        <div className={column}>
          <Chip variant="aiSection">✦ Alternatives</Chip>

          <div className="flex flex-col gap-2">
            {alternatives.map((a, i) => {
              const altId = resolveProductId(a);
              const altUrl = altId
                ? getProductDetailsUrl(altId, a.variantId ?? null, AppRoutes.PRODUCT_DETAILS)
                : null;
              return (
              <div
                key={i}
                className="flex items-center gap-3 rounded-xl border border-borderLight bg-bgLight/40 p-3"
              >
                {altUrl ? (
                  <Link to={altUrl} aria-label={`View ${a.name ?? "product"}`} className="hover:opacity-90 transition-opacity">
                    <ProductThumbnail
                      image={a.image}
                      name={a.name}
                      size="sm"
                      fallback={<IconDeviceLaptop size={24} className="text-textMuted" />}
                    />
                  </Link>
                ) : (
                  <ProductThumbnail
                    image={a.image}
                    name={a.name}
                    size="sm"
                    fallback={<IconDeviceLaptop size={24} className="text-textMuted" />}
                  />
                )}
                <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                  {altUrl ? (
                    <Link to={altUrl} className="hover:text-brandPrimary transition-colors">
                      <ClampText variant="bodySmBold" tone="dark" lines={2}>
                        {a.name ?? "—"}
                      </ClampText>
                    </Link>
                  ) : (
                    <ClampText variant="bodySmBold" tone="dark" lines={2}>
                      {a.name ?? "—"}
                    </ClampText>
                  )}
                  {a.subLabel ? (
                    <ClampText variant="bodyXsBold" tone="muted" truncate>
                      {a.subLabel}
                    </ClampText>
                  ) : null}
                  {a.price ? (
                    <Text variant="bodySmBold" tone="dark">
                      <GatedPrice>{a.price}</GatedPrice>
                    </Text>
                  ) : null}
                </div>
                {altUrl ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    rightIcon={<IconChevronRight size={14} />}
                    onClick={() => navigate(altUrl)}
                  >
                    Preview item
                  </Button>
                ) : onSendMessage ? (
                  // Items with no resolvable product (no ids at all, or a
                  // variant that no longer exists) still get the chat fallback
                  // so the button isn't dead.
                  <Button
                    variant="ghost"
                    size="sm"
                    rightIcon={<IconChevronRight size={14} />}
                    onClick={() => onSendMessage(`Tell me more about ${a.name}`)}
                  >
                    Preview item
                  </Button>
                ) : null}
              </div>
              );
            })}
          </div>

          <div className="mt-auto pt-1">
            <Button
              variant="secondary"
              fullWidth
              onClick={() => onSendMessage?.("Show me more product options")}
            >
              View more products
            </Button>
          </div>
        </div>
      ) : null}

      {notes.length > 0 ? (
        <div className={column}>
          <Chip variant="aiSection">✦ Procurtal Notes:</Chip>
          <ul className="flex flex-col gap-2 pl-4">
            {notes.map((note, i) => (
              <li key={i} className="list-disc">
                <Text variant="bodySm" tone="body" inline>
                  {note}
                </Text>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

// ── Dual range slider ─────────────────────────────────────────────────

function DualRangeSlider({
  absMin,
  absMax,
  valueMin,
  valueMax,
  onChangeMin,
  onChangeMax,
}: {
  absMin: number;
  absMax: number;
  valueMin: number;
  valueMax: number;
  onChangeMin: (v: number) => void;
  onChangeMax: (v: number) => void;
}) {
  const range = absMax - absMin || 1;
  const pctMin = ((valueMin - absMin) / range) * 100;
  const pctMax = ((valueMax - absMin) / range) * 100;
  // When both thumbs overlap, put the min thumb on top so the user can drag
  // it left (otherwise only the max thumb — rendered later in the DOM — gets clicks).
  const minZ = valueMin >= valueMax ? 5 : 3;
  const maxZ = valueMin >= valueMax ? 3 : 5;

  return (
    <div className="dual-range-wrap">
      {/* Custom track — sits behind both inputs */}
      <div className="pointer-events-none absolute left-0 right-0 top-1/2 z-0 h-[6px] -translate-y-1/2 rounded-full bg-borderDefault">
        <div
          className="absolute inset-y-0 rounded-full bg-brandPrimary"
          style={{ left: `${pctMin}%`, right: `${100 - pctMax}%` }}
        />
      </div>
      <input
        type="range"
        min={absMin}
        max={absMax}
        value={valueMin}
        style={{ zIndex: minZ }}
        onChange={(e) => {
          const v = Number(e.target.value);
          if (v <= valueMax) onChangeMin(v);
        }}
      />
      <input
        type="range"
        min={absMin}
        max={absMax}
        value={valueMax}
        style={{ zIndex: maxZ }}
        onChange={(e) => {
          const v = Number(e.target.value);
          if (v >= valueMin) onChangeMax(v);
        }}
      />
    </div>
  );
}

// ── Refine search ─────────────────────────────────────────────────────

function ChipSelect({
  options,
  selected,
  onToggle,
  multi = true,
}: {
  options: string[];
  selected: string[];
  onToggle: (value: string) => void;
  multi?: boolean;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => {
        const on = selected.includes(opt);
        return (
          <button
            key={opt}
            type="button"
            onClick={() => onToggle(opt)}
            className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 transition-colors ${
              on
                ? "bg-brandPrimary text-textOnDark"
                : "border border-borderDefault bg-bgWhite text-textBody hover:border-borderDark"
            }`}
          >
            {on && multi ? (
              <IconCheck size={12} className="text-textOnDark" />
            ) : null}
            <Text variant={on ? "bodySmBold" : "bodySm"} tone="inherit" inline>{opt}</Text>
          </button>
        );
      })}
    </div>
  );
}

function FilterSection({
  label,
  children,
  defaultOpen = false,
}: {
  label: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="flex flex-col">
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        className="flex items-center justify-between py-2"
      >
        <Text variant="bodySmBold" tone="dark">{label}</Text>
        {open
          ? <IconChevronUp size={16} className="text-textMuted" />
          : <IconChevronDown size={16} className="text-textMuted" />}
      </button>
      {open ? <div className="pb-3 pt-1">{children}</div> : null}
    </div>
  );
}

function RefineSearchWidget({
  data,
  onSendMessage,
}: {
  data: RefineSearchData;
  onSendMessage?: (text: string, productId?: number) => void;
}) {
  const { data: facets } = useCatalogFacets();
  const useCases = data.useCases ?? [];
  const leadTimeOptions = data.leadTimeOptions ?? [];
  const budget = data.budgetRange;
  const moq = data.moqRange;
  const absMin = budget?.min ?? 500;
  const absMax = budget?.max ?? 5000;
  const currency = budget?.currency ?? "QAR";
  const moqSuggested = moq?.max ?? moq?.min ?? 20;
  const moqAbsMax = Math.max(moqSuggested * 2, 50);
  const moqUnit = moq?.unit ?? "units";

  const [selectedUseCases, setSelectedUseCases] = useState<string[]>([]);
  const [selectedBrands, setSelectedBrands] = useState<string[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedRam, setSelectedRam] = useState<string[]>([]);
  const [selectedStorage, setSelectedStorage] = useState<string[]>([]);
  const [selectedCpu, setSelectedCpu] = useState<string[]>([]);
  const [selectedScreenSizes, setSelectedScreenSizes] = useState<string[]>([]);
  const [selectedColors, setSelectedColors] = useState<string[]>([]);
  const [selectedConditions, setSelectedConditions] = useState<string[]>([]);
  const [selectedLeadTime, setSelectedLeadTime] = useState<string | null>(null);
  const [budgetMin, setBudgetMin] = useState(absMin);
  const [budgetMax, setBudgetMax] = useState(absMax);
  const [moqMax, setMoqMax] = useState(moqSuggested);

  const toggleMulti = (
    value: string,
    setSelected: React.Dispatch<React.SetStateAction<string[]>>,
  ) => setSelected((prev) =>
    prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
  );

  const toggleSingle = (
    value: string,
    selected: string | null,
    setSelected: React.Dispatch<React.SetStateAction<string | null>>,
  ) => setSelected(selected === value ? null : value);

  const handleRefine = () => {
    if (!onSendMessage) return;
    const parts: string[] = [];
    if (selectedUseCases.length) parts.push(`use case: ${selectedUseCases.join(", ")}`);
    if (selectedBrands.length) parts.push(`brands: ${selectedBrands.join(", ")}`);
    if (selectedCategories.length) parts.push(`categories: ${selectedCategories.join(", ")}`);
    if (selectedRam.length) parts.push(`RAM: ${selectedRam.join(", ")}`);
    if (selectedStorage.length) parts.push(`storage: ${selectedStorage.join(", ")}`);
    if (selectedCpu.length) parts.push(`CPU: ${selectedCpu.join(", ")}`);
    if (selectedScreenSizes.length) parts.push(`screen size: ${selectedScreenSizes.join(", ")}`);
    if (selectedColors.length) parts.push(`color: ${selectedColors.join(", ")}`);
    if (selectedConditions.length) parts.push(`condition: ${selectedConditions.join(", ")}`);
    if (selectedLeadTime) parts.push(`lead time: ${selectedLeadTime}`);
    if (budget) parts.push(`budget: ${currency} ${budgetMin.toLocaleString()}–${budgetMax.toLocaleString()}`);
    parts.push(`maximum order quantity: up to ${moqMax} ${moqUnit}`);
    onSendMessage(parts.length
      ? `Refine my recommendations — ${parts.join("; ")}`
      : "Refine my recommendations");
  };

  const LEAD_TIME_FAST = new Set(["< 1 week", "≤ 7 days", "1 week"]);

  const selectedCount = selectedUseCases.length + selectedBrands.length + selectedCategories.length
    + selectedRam.length + selectedStorage.length + selectedCpu.length
    + selectedScreenSizes.length + selectedColors.length + selectedConditions.length
    + (selectedLeadTime ? 1 : 0);

  return (
    <div className="flex w-full flex-col gap-3 rounded-2xl border border-borderDefault bg-bgWhite p-5">
      <div className="self-start">
        <Chip variant="aiSection">✦ {data.title ?? "Refine your search"}</Chip>
      </div>

      <Heading variant="h3" tone="dark">
        Narrow down your results
      </Heading>
      <Text variant="bodySm" tone="muted">
        Pick the filters that matter most — everything is optional.
      </Text>

      <div className="flex flex-col divide-y divide-borderDefault">
        {/* USE CASE */}
        {useCases.length > 0 ? (
          <FilterSection label="Use case" defaultOpen>
            <ChipSelect
              options={useCases}
              selected={selectedUseCases}
              onToggle={(v) => toggleMulti(v, setSelectedUseCases)}
            />
          </FilterSection>
        ) : null}

        {/* BRAND */}
        {facets?.brands?.length ? (
          <FilterSection label="Brand" defaultOpen>
            <ChipSelect
              options={facets.brands}
              selected={selectedBrands}
              onToggle={(v) => toggleMulti(v, setSelectedBrands)}
            />
          </FilterSection>
        ) : null}

        {/* CATEGORY */}
        {facets?.categories?.length ? (
          <FilterSection label="Category">
            <ChipSelect
              options={facets.categories}
              selected={selectedCategories}
              onToggle={(v) => toggleMulti(v, setSelectedCategories)}
            />
          </FilterSection>
        ) : null}

        {/* BUDGET */}
        {budget ? (
          <FilterSection label="Budget per unit" defaultOpen>
            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-end">
                <Text variant="bodySmBold" tone="dark">
                  {currency} {budgetMin.toLocaleString()} – {currency} {budgetMax.toLocaleString()}
                </Text>
              </div>
              <DualRangeSlider
                absMin={absMin}
                absMax={absMax}
                valueMin={budgetMin}
                valueMax={budgetMax}
                onChangeMin={setBudgetMin}
                onChangeMax={setBudgetMax}
              />
            </div>
          </FilterSection>
        ) : null}

        {/* RAM */}
        {facets?.ramOptions?.length ? (
          <FilterSection label="RAM">
            <ChipSelect
              options={facets.ramOptions}
              selected={selectedRam}
              onToggle={(v) => toggleMulti(v, setSelectedRam)}
            />
          </FilterSection>
        ) : null}

        {/* STORAGE */}
        {facets?.storageOptions?.length ? (
          <FilterSection label="Storage">
            <ChipSelect
              options={facets.storageOptions}
              selected={selectedStorage}
              onToggle={(v) => toggleMulti(v, setSelectedStorage)}
            />
          </FilterSection>
        ) : null}

        {/* CPU */}
        {facets?.cpuOptions?.length ? (
          <FilterSection label="Processor">
            <ChipSelect
              options={facets.cpuOptions}
              selected={selectedCpu}
              onToggle={(v) => toggleMulti(v, setSelectedCpu)}
            />
          </FilterSection>
        ) : null}

        {/* SCREEN SIZE */}
        {facets?.screenSizes?.length ? (
          <FilterSection label="Screen size">
            <ChipSelect
              options={facets.screenSizes}
              selected={selectedScreenSizes}
              onToggle={(v) => toggleMulti(v, setSelectedScreenSizes)}
            />
          </FilterSection>
        ) : null}

        {/* COLOR */}
        {facets?.colors?.length ? (
          <FilterSection label="Color">
            <ChipSelect
              options={facets.colors}
              selected={selectedColors}
              onToggle={(v) => toggleMulti(v, setSelectedColors)}
            />
          </FilterSection>
        ) : null}

        {/* CONDITION */}
        {facets?.conditions?.length ? (
          <FilterSection label="Condition">
            <ChipSelect
              options={facets.conditions}
              selected={selectedConditions}
              onToggle={(v) => toggleMulti(v, setSelectedConditions)}
            />
          </FilterSection>
        ) : null}

        {/* LEAD TIME */}
        {leadTimeOptions.length > 0 ? (
          <FilterSection label="Lead time">
            <div className="flex flex-wrap gap-2">
              {leadTimeOptions.map((opt) => {
                const on = selectedLeadTime === opt;
                const isFast = LEAD_TIME_FAST.has(opt);
                return (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => toggleSingle(opt, selectedLeadTime, setSelectedLeadTime)}
                    className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 transition-colors ${
                      on
                        ? "bg-brandPrimary text-textOnDark"
                        : "border border-borderDefault bg-bgWhite text-textBody hover:border-borderDark"
                    }`}
                  >
                    {isFast ? <IconBolt size={14} className={on ? "text-textOnDark" : "text-accentCyan700"} /> : null}
                    <Text variant={on ? "bodySmBold" : "bodySm"} tone="inherit" inline>{opt}</Text>
                  </button>
                );
              })}
            </div>
          </FilterSection>
        ) : null}

        {/* MINIMUM ORDER QUANTITY */}
        <FilterSection label="Min. order quantity">
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-end">
              <Text variant="bodySmBold" tone="dark">Up to {moqMax} {moqUnit}</Text>
            </div>
            <input
              type="range"
              min={1}
              max={moqAbsMax}
              value={moqMax}
              className="moq-slider"
              style={{ ["--pct" as string]: `${((moqMax - 1) / (moqAbsMax - 1)) * 100}%` }}
              onChange={(e) => setMoqMax(Number(e.target.value))}
            />
          </div>
        </FilterSection>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between gap-3 pt-2">
        <button
          type="button"
          onClick={() => onSendMessage?.("Show all options — skip filters")}
          className="text-textMuted transition-colors hover:text-textBody"
        >
          <Text variant="bodySm" tone="inherit" inline>Skip — show all options</Text>
        </button>
        <Button variant="primary" onClick={handleRefine}>
          Refine recommendations{selectedCount > 0 ? ` (${selectedCount})` : ""}
        </Button>
      </div>
    </div>
  );
}

// ── Product detail ────────────────────────────────────────────────────

function ProductDetailWidget({
  data,
  onSendMessage,
}: {
  data: ProductDetailData;
  onSendMessage?: (text: string, productId?: number) => void;
}) {
  const navigate = useNavigate();
  const specEntries = Object.entries(data.specs ?? {});
  const questions = data.followUpQuestions ?? [];

  const specLine =
    data.specLine ??
    specEntries
      .filter(([k]) => ["Processor", "RAM", "Storage", "Display", "OS"].includes(k))
      .map(([, v]) => v)
      .join(" · ");

  return (
    <div className="flex w-full flex-col gap-4 rounded-2xl border border-borderDefault bg-bgWhite p-5">
      {/* Header: badge + name | availability */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-3">
          <Chip variant="aiSection">✦ {data.badge ?? "Chatting about"}</Chip>
          <Heading variant="h2" tone="dark">{data.name ?? "—"}</Heading>
        </div>
        {data.availability ? (
          <span className="flex items-center gap-1.5 rounded-full border border-accentGreen bg-accentGreenTint px-3 py-1.5">
            <IconCircleCheck size={14} className="shrink-0 text-accentGreen" />
            <span className="text-accentGreenDark">
              <Text variant="bodySmBold" tone="inherit" inline>{data.availability}</Text>
            </span>
          </span>
        ) : null}
      </div>

      {/* Product hero card — nested sub-card, lighter bg so it reads as a
          section inside the outer widget card rather than a duplicate panel. */}
      <div className="flex flex-wrap items-center gap-4 rounded-2xl border border-borderLight bg-bgLight/40 p-4">
        {/* Product image */}
        <ProductThumbnail
          image={data.image}
          name={data.name}
          size="lg"
          fallback={<IconDeviceLaptop size={40} className="text-textMuted" />}
        />

        {/* Info */}
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <Text variant="bodyBold" tone="dark">{data.name ?? "—"}</Text>
          {specLine ? (
            <Text variant="bodySm" tone="muted">{specLine}</Text>
          ) : null}
          {data.price ? (
            <div className="mt-1 flex flex-wrap items-baseline gap-2">
              <Text variant="bodyBold" tone="dark">
                <GatedPrice>{data.price}</GatedPrice>
              </Text>
              <Text variant="bodySm" tone="muted" inline>/unit</Text>
              {data.originalPrice ? (
                <del>
                  <Text variant="bodySm" tone="muted" inline>
                    <GatedPrice>{data.originalPrice}</GatedPrice>
                  </Text>
                </del>
              ) : null}
              {data.savings ? (
                <span className="rounded-full bg-accentGreenTint px-2.5 py-0.5">
                  <span className="text-accentGreenDark">
                    <Text variant="bodyXsBold" tone="inherit" inline>{data.savings}</Text>
                  </span>
                </span>
              ) : null}
            </div>
          ) : null}
        </div>

        {/* Add to cart */}
        <Button
          variant="primary"
          leftIcon={<IconShoppingCart size={16} />}
          onClick={() => onSendMessage?.(`I'd like to add ${data.name ?? "this product"} to my order`)}
        >
          Add to cart
        </Button>
      </div>

      {/* Specifications grid */}
      {specEntries.length > 0 ? (
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between gap-2">
            <Text variant="bodySmBold" tone="dark">Specifications</Text>
            <button
              type="button"
              className="flex items-center gap-1 text-brandPrimary transition-opacity hover:opacity-70"
              onClick={() => navigate(AppRoutes.PRODUCTS)}
            >
              <Text variant="bodySm" tone="inherit" inline>View full product details</Text>
              <IconArrowRight size={14} />
            </button>
          </div>
          <div className="overflow-hidden rounded-2xl border border-borderDefault">
            <div className="grid grid-cols-3">
              {specEntries.map(([key, val], i) => (
                <div
                  key={key}
                  className={`flex flex-col gap-1 p-3${i % 3 !== 2 ? " border-r border-borderLight" : ""}${i >= 3 ? " border-t border-borderLight" : ""}`}
                >
                  <Text variant="capsSm" tone="muted">{key}</Text>
                  <Text variant="bodySmBold" tone="dark">{val}</Text>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : null}

      {/* Follow-up questions */}
      {questions.length > 0 ? (
        <div className="flex flex-col gap-2">
          <Text variant="bodySmBold" tone="dark">Suggested follow-up questions</Text>
          <div className="flex flex-wrap gap-2">
            {questions.map((q) => (
              <Chip key={q} variant="tertiary" onClick={() => onSendMessage?.(q)}>
                {q}
              </Chip>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

// ── Product compare (dual card) ───────────────────────────────────────

function ProductCompareWidget({
  data,
  onSendMessage,
}: {
  data: ProductCompareData;
  onSendMessage?: (text: string, productId?: number) => void;
}) {
  const navigate = useNavigate();
  const products = (data.products ?? []).slice(0, 2);
  const questions = data.followUpQuestions ?? [];
  const resolveProductId = useWidgetProductIds(products);

  if (products.length === 0) return null;

  const title = products.map((p) => p.name ?? "—").join(" + ");

  return (
    <div className="flex w-full flex-col gap-5 rounded-2xl border border-borderDefault bg-bgWhite p-5">
      {/* Header row */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Chip variant="aiSection">✦ {data.badge ?? "Deeper look at your top 2"}</Chip>
        {data.availability ? (
          <span className="flex items-center gap-1.5 rounded-full border border-accentGreen bg-accentGreenTint px-3 py-1.5">
            <IconCircleCheck size={14} className="shrink-0 text-accentGreen" />
            <span className="text-accentGreenDark">
              <Text variant="bodySmBold" tone="inherit" inline>{data.availability}</Text>
            </span>
          </span>
        ) : null}
      </div>

      {/* Title */}
      <Heading variant="h3" tone="dark">{title}</Heading>

      {/* Products */}
      {products.map((p, i) => {
        const specEntries = Object.entries(p.specs ?? {}).slice(0, 6);
        return (
          <div key={i} className="flex flex-col gap-3">
            {/* Pick label + name */}
            <div className="flex items-center gap-3">
              <Chip variant="tertiary">PICK #{i + 1}</Chip>
              <Text variant="bodyBold" tone="dark">{p.name ?? "—"}</Text>
            </div>

            {/* Hero card */}
            <div className="flex flex-wrap items-center gap-4 rounded-2xl border border-borderDefault bg-bgWhite p-4">
              <ProductThumbnail
                image={p.image}
                name={p.name}
                size="lg"
                fallback={<IconDeviceLaptop size={40} className="text-textMuted" />}
              />
              <div className="flex min-w-0 flex-1 flex-col gap-1">
                <Text variant="bodyBold" tone="dark">{p.name ?? "—"}</Text>
                {p.specLine ? (
                  <Text variant="bodySm" tone="muted">{p.specLine}</Text>
                ) : null}
                {p.price ? (
                  <div className="mt-1 flex flex-wrap items-baseline gap-2">
                    <Text variant="bodyBold" tone="dark">
                      <GatedPrice>{p.price}</GatedPrice>
                    </Text>
                    <Text variant="bodySm" tone="muted" inline>/unit</Text>
                    {p.originalPrice ? (
                      <del>
                        <Text variant="bodySm" tone="muted" inline>
                          <GatedPrice>{p.originalPrice}</GatedPrice>
                        </Text>
                      </del>
                    ) : null}
                    {p.savings ? (
                      <span className="rounded-full bg-accentGreenTint px-2.5 py-0.5">
                        <span className="text-accentGreenDark">
                          <Text variant="bodyXsBold" tone="inherit" inline>{p.savings}</Text>
                        </span>
                      </span>
                    ) : null}
                  </div>
                ) : null}
              </div>
              <Button
                variant="primary"
                leftIcon={<IconShoppingCart size={16} />}
                onClick={() => onSendMessage?.(`I'd like to add ${p.name ?? "this product"} to my order`)}
              >
                Add to cart
              </Button>
            </div>

            {/* Specs grid */}
            {specEntries.length > 0 ? (
              <div className="overflow-hidden rounded-2xl border border-borderDefault">
                <div className="grid grid-cols-3">
                  {specEntries.map(([key, val], j) => (
                    <div
                      key={key}
                      className={`flex flex-col gap-1 p-3${j % 3 !== 2 ? " border-r border-borderLight" : ""}${j >= 3 ? " border-t border-borderLight" : ""}`}
                    >
                      <Text variant="capsSm" tone="muted">{key}</Text>
                      <Text variant="bodySmBold" tone="dark">{val}</Text>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        );
      })}

      {/* Follow-up questions */}
      {questions.length > 0 ? (
        <div className="flex flex-col gap-2">
          <Text variant="bodySmBold" tone="dark">Suggested follow-up questions</Text>
          <div className="flex flex-wrap gap-2">
            {questions.map((q) => (
              <Chip key={q} variant="tertiary" onClick={() => onSendMessage?.(q)}>
                {q}
              </Chip>
            ))}
          </div>
        </div>
      ) : null}

      {/* Footer */}
      <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
        {data.note ? (
          <div className="flex items-center gap-2">
            <span aria-hidden>💡</span>
            <Text variant="bodySm" tone="muted" inline>{data.note}</Text>
          </div>
        ) : null}
        <Button
          variant="primary"
          rightIcon={<IconArrowRight size={16} />}
          onClick={() => {
            // Compare page expects product template ids, not variant ids.
            const ids = products
              .map(resolveProductId)
              .filter((id): id is number => id != null);
            const url = ids.length >= 2
              ? `${AppRoutes.PRODUCTS_COMPARE}?ids=${ids.join(",")}&from=ai`
              : AppRoutes.PRODUCTS_COMPARE;
            navigate(url);
          }}
        >
          Open side-by-side comparison
        </Button>
      </div>
    </div>
  );
}

// ── Results (table-like) ─────────────────────────────────────────────

function ResultsWidget({ data }: { data: ResultsData }) {
  const rows = data.rows ?? [];
  return (
    <WidgetCard title={data.title}>
      <div className="flex flex-col divide-y divide-borderLight">
        {rows.map((r, i) => (
          <div key={i} className="flex items-center justify-between gap-4 py-2">
            <Text variant="bodySm" tone="muted" inline>
              {r.label}
            </Text>
            <div className="flex items-center gap-2">
              <Text variant="bodySmBold" tone="dark" inline>
                {r.value}
              </Text>
              {r.status && r.status !== "normal" ? <StatusDot status={r.status} /> : null}
            </div>
          </div>
        ))}
      </div>
    </WidgetCard>
  );
}

function StatusDot({ status }: { status: "high" | "low" }) {
  const tone = status === "high" ? "bg-accentGreen text-bgWhite" : "bg-accentError text-bgWhite";
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider ${tone}`}>
      {status}
    </span>
  );
}

// ── Bar chart ────────────────────────────────────────────────────────

function BarChartWidget({ data }: { data: ChartData }) {
  const points = data.points ?? [];
  const max = data.maxValue ?? Math.max(1, ...points.map((p) => Number(p.value ?? 0)));
  return (
    <WidgetCard title={data.title}>
      <div className="flex flex-col gap-2">
        {points.map((p, i) => {
          const v = Number(p.value ?? 0);
          const pct = Math.max(0, Math.min(100, (v / max) * 100));
          return (
            <div key={i} className="flex items-center gap-3">
              <div className="w-24 shrink-0">
                <Text variant="bodyXs" tone="muted" inline>
                  {p.label}
                </Text>
              </div>
              <div className="relative h-5 flex-1 overflow-hidden rounded-md bg-bgLight">
                <div
                  className="h-full bg-gradientBrand"
                  style={{ width: `${pct}%` }}
                />
              </div>
              <div className="w-20 text-right">
                <Text variant="bodyXsBold" tone="dark" inline>
                  {v}
                  {data.unit ? ` ${data.unit}` : ""}
                </Text>
              </div>
            </div>
          );
        })}
      </div>
    </WidgetCard>
  );
}

// ── Line chart ───────────────────────────────────────────────────────

function LineChartWidget({ data }: { data: ChartData }) {
  const points = data.points ?? [];
  if (points.length === 0) return <WidgetCard title={data.title}>—</WidgetCard>;

  const W = 520;
  const H = 160;
  const padX = 32;
  const padY = 16;
  const max = data.maxValue ?? Math.max(1, ...points.map((p) => Number(p.value ?? 0)));
  const min = Math.min(0, ...points.map((p) => Number(p.value ?? 0)));
  const range = max - min || 1;
  const stepX = points.length > 1 ? (W - padX * 2) / (points.length - 1) : 0;

  const coords = points.map((p, i) => {
    const x = padX + i * stepX;
    const y = padY + ((max - Number(p.value ?? 0)) / range) * (H - padY * 2);
    return { x, y, value: Number(p.value ?? 0), label: p.label };
  });
  const path = coords.map((c, i) => `${i === 0 ? "M" : "L"} ${c.x} ${c.y}`).join(" ");
  const areaPath = `${path} L ${coords[coords.length - 1]!.x} ${H - padY} L ${coords[0]!.x} ${H - padY} Z`;

  return (
    <WidgetCard title={data.title}>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full">
        <defs>
          <linearGradient id="lc-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--color-accent-cyan)" stopOpacity="0.35" />
            <stop offset="100%" stopColor="var(--color-accent-cyan)" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={areaPath} fill="url(#lc-fill)" />
        <path d={path} fill="none" stroke="var(--color-accent-cyan-700)" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        {coords.map((c, i) => (
          <circle key={i} cx={c.x} cy={c.y} r="3" fill="var(--color-accent-cyan-700)" />
        ))}
        {coords.map((c, i) => (
          <text
            key={`l-${i}`}
            x={c.x}
            y={H - 2}
            textAnchor="middle"
            fontSize="10"
            fill="var(--color-text-muted)"
          >
            {c.label}
          </text>
        ))}
      </svg>
      {data.unit ? (
        <Text variant="bodyXs" tone="muted">
          Unit: {data.unit}
        </Text>
      ) : null}
    </WidgetCard>
  );
}

// ── Report card (scatter + metrics) ──────────────────────────────────

function ReportCardWidget({ data }: { data: ReportCardData }) {
  const points = data.points ?? [];
  const metrics = data.metrics ?? [];
  const W = 280;
  const H = 160;
  const xs = points.map((p) => Number(p.x ?? 0));
  const ys = points.map((p) => Number(p.y ?? 0));
  const xMin = Math.min(0, ...xs);
  const xMax = Math.max(1, ...xs);
  const yMin = Math.min(0, ...ys);
  const yMax = Math.max(1, ...ys);
  const xRange = xMax - xMin || 1;
  const yRange = yMax - yMin || 1;
  const pad = 12;

  return (
    <WidgetCard title={data.title}>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-stretch">
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full max-w-[320px] rounded-xl bg-bgLight">
          <line x1={pad} y1={H - pad} x2={W - pad} y2={H - pad} stroke="var(--color-border-default)" strokeWidth="1" />
          <line x1={pad} y1={pad} x2={pad} y2={H - pad} stroke="var(--color-border-default)" strokeWidth="1" />
          {points.map((p, i) => {
            const cx = pad + ((Number(p.x ?? 0) - xMin) / xRange) * (W - pad * 2);
            const cy = H - pad - ((Number(p.y ?? 0) - yMin) / yRange) * (H - pad * 2);
            return (
              <circle
                key={i}
                cx={cx}
                cy={cy}
                r={p.latest ? 6 : 3.5}
                fill={p.latest ? "var(--color-accent-cyan-700)" : "var(--color-accent-cyan)"}
                stroke={p.latest ? "var(--color-bg-white)" : "none"}
                strokeWidth={p.latest ? 2 : 0}
              />
            );
          })}
        </svg>
        <div className="grid flex-1 grid-cols-2 gap-3">
          {metrics.map((m, i) => (
            <div key={i} className="flex flex-col gap-1 rounded-xl border border-borderLight p-3">
              <Text variant="bodyXs" tone="muted">
                {m.label}
              </Text>
              <Text variant="bodyBold" tone="dark">
                {m.value}
              </Text>
            </div>
          ))}
        </div>
      </div>
    </WidgetCard>
  );
}

// ── Text options ─────────────────────────────────────────────────────

function TextOptionsWidget({ data }: { data: TextOptionsData }) {
  return (
    <WidgetCard title={data.title}>
      {data.subtitle ? (
        <Text variant="bodySm" tone="muted">
          {data.subtitle}
        </Text>
      ) : null}
      {data.paragraphs?.map((p, i) => (
        <Text key={`p-${i}`} variant="body" tone="body">
          {p}
        </Text>
      ))}
      {data.bullets && data.bullets.length > 0 ? (
        <ul className="ml-5 list-disc">
          {data.bullets.map((b, i) => (
            <li key={`b-${i}`} className="mb-1">
              <Text variant="bodySm" tone="body" inline>
                {b}
              </Text>
            </li>
          ))}
        </ul>
      ) : null}
    </WidgetCard>
  );
}

// ── Action button ────────────────────────────────────────────────────

function ActionWidget({ data }: { data: ActionData }) {
  const navigate = useNavigate();
  const onClick = () => {
    if (data.action === "navigate_products") navigate(AppRoutes.PRODUCTS);
    else if (data.action === "navigate_compare") navigate(AppRoutes.PRODUCTS_COMPARE);
  };
  return (
    <Button variant="primary" rightIcon={<IconArrowRight size={16} />} onClick={onClick}>
      {data.label ?? "Continue"}
    </Button>
  );
}

// ── Comparison table ─────────────────────────────────────────────────

const PRICE_SPEC_KEYS = new Set(["price", "Price", "unit price", "Unit Price"]);

function ComparisonTableWidget({ data }: { data: ComparisonTableData }) {
  const products = data.products ?? [];
  const specKeys =
    data.specOrder ?? Object.keys(products[0]?.specs ?? {});

  if (products.length === 0 || specKeys.length === 0) return null;

  return (
    <WidgetCard title={data.title}>
      <div className="overflow-x-auto">
        <table className="w-full min-w-max border-collapse text-left">
          <thead>
            <tr className="bg-bgLight">
              <th className="sticky left-0 z-10 bg-bgLight px-3 py-2">
                <Text variant="bodyXsBold" tone="muted" inline>
                  Spec
                </Text>
              </th>
              {products.map((p, i) => (
                <th key={i} className="px-3 py-2">
                  <Text variant="bodySmBold" tone="dark" inline>
                    {p.name ?? `Product ${i + 1}`}
                  </Text>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-borderLight">
            {specKeys.map((key) => {
              const isPrice = PRICE_SPEC_KEYS.has(key);
              return (
                <tr key={key} className="even:bg-bgLight/40">
                  <td className="sticky left-0 z-10 bg-bgWhite px-3 py-2 even:bg-bgLight/40">
                    <Text variant="bodySm" tone="muted" inline>
                      {key}
                    </Text>
                  </td>
                  {products.map((p, i) => {
                    const value = p.specs?.[key] ?? "—";
                    return (
                      <td key={i} className="px-3 py-2">
                        <Text variant="bodySmBold" tone="dark" inline>
                          {isPrice ? <GatedPrice>{value}</GatedPrice> : value}
                        </Text>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </WidgetCard>
  );
}
